import { useState } from 'react'
import { Plus, Edit2, Trash2 } from 'lucide-react'
import OrgModal from '../../components/organizations/OrgModal'
import ConfirmDialog from '../../components/common/ConfirmDialog'
import OrgDetail from './OrgDetail'
import { Card, PlanBadge, StatusChip, naira, orgMrr, orgAccess } from '../ui'

const FILTERS = [
  { key: 'all', label: 'All', test: () => true },
  { key: 'trialing', label: 'Trialing', test: (o) => ['trialing', 'ending'].includes(orgAccess(o).key) },
  { key: 'active', label: 'Active', test: (o) => orgAccess(o).key === 'active' },
  { key: 'risk', label: 'At risk', test: (o) => ['past_due', 'cancelled', 'expired', 'ending'].includes(orgAccess(o).key) },
]

export default function Organizations({ organizations, branches, users, onManage, onAdd, onUpdate, onDelete, showToast, usage, onSuspend, onRestore }) {
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState('all')
  const [modal, setModal] = useState(null)       // null | { org: null|org }
  const [deleting, setDeleting] = useState(null) // org
  const [detailOrg, setDetailOrg] = useState(null)

  const visible = organizations
    .filter(FILTERS.find((f) => f.key === filter).test)
    .filter((o) => o.name.toLowerCase().includes(query.trim().toLowerCase()))

  async function handleSave(orgData, orgBranches) {
    const result = modal.org
      ? await onUpdate(modal.org.id, orgData, orgBranches)
      : await onAdd(orgData, orgBranches)
    showToast(result?.success === false ? result.message : (modal.org ? 'Organization updated.' : 'Organization created.'),
      result?.success === false ? 'error' : 'success')
    setModal(null)
  }

  async function handleDelete() {
    const org = deleting
    setDeleting(null)
    const result = await onDelete(org.id)
    showToast(result?.success === false ? result.message : `"${org.name}" deleted.`,
      result?.success === false ? 'error' : 'success')
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search organizations…"
          className="px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-sm outline-none focus:border-teal-500 w-56" />
        {FILTERS.map((f) => {
          const n = organizations.filter(f.test).length
          return (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                filter === f.key ? 'border-teal-600 bg-teal-50 text-teal-700' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
              }`}>
              {f.label} · {n}
            </button>
          )
        })}
        <span className="flex-1" />
        <button onClick={() => setModal({ org: null })}
          className="flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold px-3.5 py-2 rounded-lg transition">
          <Plus className="w-3.5 h-3.5" /> New organization
        </button>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm tabular-nums">
          <thead>
            <tr className="text-[10.5px] font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100">
              <th className="text-left px-4 py-2.5">Organization</th>
              <th className="text-left px-4 py-2.5">Plan</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5">Branches</th>
              <th className="text-right px-4 py-2.5">MRR</th>
              <th className="text-left px-4 py-2.5">Trial ends</th>
              <th className="px-2 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-sm text-gray-400">No organizations match.</td></tr>
            )}
            {visible.map((org) => {
              const branchCount = branches.filter((b) => b.organizationId === org.id).length
              const mrr = orgMrr(org)
              return (
                <tr key={org.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition">
                  <td className="px-4 py-3">
                    <button onClick={() => setDetailOrg(org)} className="text-left group">
                      <p className="font-semibold text-gray-800 group-hover:text-teal-700 group-hover:underline transition">{org.name}</p>
                      <p className="text-xs text-gray-400">
                        {org.location}
                        {org.createdAt && ` · joined ${org.createdAt.toLocaleDateString('en-NG', { month: 'short', year: 'numeric' })}`}
                      </p>
                    </button>
                  </td>
                  <td className="px-4 py-3"><PlanBadge plan={org.plan} /></td>
                  <td className="px-4 py-3"><StatusChip org={org} /></td>
                  <td className="px-4 py-3 text-right">{branchCount}</td>
                  <td className="px-4 py-3 text-right">{mrr > 0 ? naira(mrr) : '—'}</td>
                  <td className="px-4 py-3 text-gray-500">
                    {org.trialEndsAt
                      ? org.trialEndsAt.toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })
                      : '—'}
                  </td>
                  <td className="px-2 py-3 whitespace-nowrap">
                    <button onClick={() => onManage(org)}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition">
                      Manage
                    </button>
                    <button onClick={() => setModal({ org })} title="Edit organization"
                      className="p-1.5 ml-1 hover:bg-blue-50 text-blue-600 rounded-lg transition">
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setDeleting(org)} title="Delete organization"
                      className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg transition">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </Card>

      {detailOrg && (
        <OrgDetail
          org={detailOrg}
          branches={branches}
          users={users}
          onManage={onManage}
          onClose={() => setDetailOrg(null)}
          usage={usage}
          onSuspend={onSuspend}
          onRestore={onRestore}
        />
      )}

      {modal && (
        <OrgModal
          org={modal.org}
          existingBranches={modal.org ? branches.filter((b) => b.organizationId === modal.org.id) : []}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}

      {deleting && (() => {
        const n = branches.filter((b) => b.organizationId === deleting.id).length
        return (
          <ConfirmDialog
            message={n > 0
              ? `Delete "${deleting.name}"? This will also remove ${n} branch(es). This cannot be undone.`
              : `Delete "${deleting.name}"? This cannot be undone.`}
            onConfirm={handleDelete}
            onCancel={() => setDeleting(null)}
          />
        )
      })()}
    </div>
  )
}
